import axios, { AxiosRequestConfig } from 'axios'
import { get } from 'lodash'
import * as moment from 'moment'

import {
  camelCaseObjectKeys,
  snakeCaseObjectKeys,
} from '@doordash/object-utils'

import {
  formatDeliveryIssueDetails,
  formatDeliveryIssuesList,
  sortDeliveryIssuesListByNewest,
} from './components/organisms/delivery-issues/utilities'
import { GUARANTEED_EARNINGS_WRONG_ZIP_ERROR } from './constants'
import { geocode } from './utilities/geocode'
import { getName } from './utilities/getName'

import {
  DeliveryIssue,
  DeliveryIssueListItem,
  FastPay,
  VehiclePatchData,
} from './models/types'

export const submitDasherApplication = async applicationData => {
  const url = '/v1/dasher_applicants/'
  try {
    const response = await axios.post(url, snakeCaseObjectKeys(applicationData))
    return camelCaseObjectKeys(response.data)
  } catch (error) {
    const responseData = get(error, 'response.data')
    if (
      responseData &&
      responseData.error === GUARANTEED_EARNINGS_WRONG_ZIP_ERROR
    ) {
      const wrongZipError = { ...error.response.data, applicationData }
      throw wrongZipError
    } else if (responseData) {
      throw { fieldErrors: camelCaseObjectKeys(responseData) }
    }
    throw error
  }
}

export const findDasherApplication = async applicationCredentials => {
  const url = '/v1/dasher_applicants/'
  try {
    const response = await axios.get(url, {
      params: snakeCaseObjectKeys(applicationCredentials),
    })
    return camelCaseObjectKeys(response.data)
  } catch (error) {
    const errorCode = get(error, 'response.status')
    const errorString =
      'This email address and phone combination does not match.'

    if (errorCode === 500) {
      throw errorString
    }

    const responseData = get(error, 'response.data')
    if (responseData && typeof responseData === 'object') {
      throw { fieldErrors: camelCaseObjectKeys(responseData) }
    } else if (responseData && typeof responseData === 'string') {
      throw responseData
    }
    throw error
  }
}

export const getLocationFromZip = async zip => {
  try {
    const value = await geocode({ address: zip })
    const address_components = value[0].address_components
    let city = ''
    let state = ''
    let country = ''
    address_components.forEach(element => {
      if (element.types.includes('locality')) {
        city = element.long_name
      }
      if (element.types.includes('administrative_area_level_1')) {
        state = element.short_name
      }
      if (element.types.includes('country')) {
        country = element.short_name
      }
    })
    return { city, state, country }
  } catch (error) {
    throw error
  }
}

export const loadApplication = async (uniqueLink, utmSource = null) => {
  const url = `/v2/driver_applicant/${uniqueLink}/status/`
  try {
    const response = await axios.get(url, { params: { utm_source: utmSource } })
    return camelCaseObjectKeys(response.data)
  } catch (error) {
    throw error
  }
}

const Authorization = 'Authorization'

export const authToken = async (email, password) => {
  const url = '/v1/auth-token/'
  try {
    const response = await axios.post(url, { email, password })
    axios.defaults.headers.common[
      Authorization
    ] = `token ${response.data.token}`
    return response
  } catch (error) {
    throw error
  }
}

export const login = async (email, password) => {
  const url = '/v2/auth/web_login/'
  try {
    return await axios.post(url, { email, password })
  } catch (error) {
    throw error
  }
}

export const logout = async () => {
  const url = '/v2/auth/web_logout/'
  try {
    return await axios.post(url, {})
  } catch (error) {
    throw error
  }
}

export const loadConsumer = async () => {
  const url = '/v2/consumer/me/'
  return await axios.get(url)
}

export const loadDasher = async () => {
  const url = '/v1/dashers/me/?fields=applicant_unique_link'
  return await axios.get(url)
}

export const loadDasherSupplementalOnboarding = async () => {
  const url =
    '/v1/dashers/me/?fields=needs_supplemental_onboarding_user_experience'
  return await axios.get(url)
}

/**
 * TODO: make a generic loadDasher function that takes query params.
 */
export const loadDasherId = async () => {
  const url = '/v1/dashers/me/?fields=id'
  return await axios.get(url)
}

export const createConsumerAccount = async consumerData => {
  const url = '/v2/consumer/'
  const {
    // tslint:disable:variable-name
    firstName: first_name,
    lastName: last_name,
    altFirstName: alt_first_name,
    altLastName: alt_last_name,
    middleName: middle_name,
    phoneNumber: phone_number,
    // tslint:enable:variable-name
    email,
    password,
  } = consumerData

  // For Japanese kana names, in theory we could make this more generic,
  // but no plan currently for other regions to have alternate names
  const alternate_names = [{
    name_type: 'kana',
    first_name: alt_first_name,
    last_name: alt_last_name,
  }]

  return await axios.post(url, {
    email,
    password,
    first_name,
    last_name,
    middle_name,
    phone_number,
    alternate_names,
  })
}

export const updateDasherApplicant = async (uniqueLink, args) => {
  const {
    firstName,
    lastName,
    altFirstName,
    altLastName,
    middleName,
    birthday,
    referredBy,
    vehicleType,
    licenseNumber,
    licenseState,
    insuranceCheck,
    australianBusinessNumber,
    isGstRegistered,
    utmSource,
    bankAccountToken,
    offerLetterSigned,
    citizenship,
  } = args

  const alternate_names = (altFirstName && altLastName) ? [{
    name_type: 'kana',
    first_name: altFirstName,
    last_name: altLastName,
  }] : undefined

  const insuranceCheckDate = insuranceCheck
    ? moment().format('YYYY-MM-DD')
    : insuranceCheck

  const body = {
    middle_name_explicit: middleName || undefined,
    date_of_birth: birthday,
    referral_phone_or_code: referredBy || undefined,
    vehicle_type: vehicleType,
    license_number: licenseNumber,
    license_state: licenseState,
    insurance_check_date: insuranceCheckDate,
    australian_business_number: australianBusinessNumber || undefined,
    is_gst_registered: isGstRegistered || undefined,
    utm_source: utmSource || undefined,
    bank_account_token: bankAccountToken || undefined,
    offer_letter_signed: offerLetterSigned || undefined,
    citizenship_country_shortname: citizenship || undefined,
    alternate_names,
    // if we are submitting japanese names, submit first_name & last_name instead of the pre-constructed name
    // this is a hacky way to identify japanese name submission, but eventually passing first_name & last_name should
    // replace passing name parameter entirely, so this check can be removed. dont want to block japan flow on this
    // being migrated for all flows.
    name: alternate_names ? undefined : getName(firstName, middleName, lastName),
    first_name: alternate_names ? firstName : undefined,
    last_name: alternate_names ? lastName : undefined,
    no_middle_name_affirmation: alternate_names ? true : undefined,
  }


  const url = `/v1/dasher_applicants/${uniqueLink}/`
  try {
    const res = await axios.patch(url, body)
    return res
  } catch (error) {
    throw error
  }
}

export const submitBackgroundCheck = async (uniqueLink, args) => {
  const { ssn, licenseState, licenseNumber, requestedBgCheckCopy } = args

  const body = {
    ssn,
    driver_license_state: licenseState,
    driver_license_number: licenseNumber,
    copy_requested: requestedBgCheckCopy,
  }

  const url = `/v2/driver_applicant/${uniqueLink}/background_check/`

  try {
    const res = await axios.post(url, body)
    return camelCaseObjectKeys(res.data)
  } catch (error) {
    throw error
  }
}

export const submitBackgroundCheckTokenized = async (
  uniqueLink,
  data,
  form
) => {
  return new Promise((resolve, reject) => {
    form.submit(
      `/in/dasherapi/v2/driver_applicant/${uniqueLink}/background_check/`,
      {
        headers: {
          Authorization: `token ${localStorage.getItem('token')}`,
        },
        data: {
          ...data,
          use_tokenization: true,
        },
      },
      (status, response) => {
        if (status > 399) {
          reject(response)
        } else {
          resolve(response)
        }
      },
      error => {
        reject(error)
      }
    )
  })
}

export const createDasherAccount = async uniqueLink => {
  const body = {
    applicant_unique_link: uniqueLink,
  }

  const url = '/v1/dashers/'

  try {
    const res = await axios.post(url, body)
    return res
  } catch (error) {
    throw error
  }
}

export const goToW9Signing = async uniqueLink => {
  const url = `/v1/dasher_applicants/${uniqueLink}/w9`
  try {
    const response = await axios.post(url)
    return response.data.url
  } catch (error) {
    throw error
  }
}

const sendDasherApplicantUpdate = async (uniqueLink, body) => {
  const url = `/v1/dasher_applicants/${uniqueLink}/`
  try {
    const response = await axios.patch(url, body)
    return response.data
  } catch (error) {
    throw error
  }
}

export const setDropshipping = (uniqueLink, value) => {
  return sendDasherApplicantUpdate(uniqueLink, {
    use_dropshipping: value,
  })
}

export const setW9Signed = (uniqueLink, value) => {
  return sendDasherApplicantUpdate(uniqueLink, {
    w9_signed: value,
  })
}

export const submitMailingAddress = (uniqueLink, mailingAddress) => {
  return sendDasherApplicantUpdate(uniqueLink, {
    mailing_address: {
      ...mailingAddress,
      street:
        typeof mailingAddress.streetLine2 !== 'undefined'
          ? `${mailingAddress.street} ${mailingAddress.streetLine2}`
          : mailingAddress.street,
    },
  })
}

export const bookOrientation = async (uniqueLink, orientationId) => {
  const url = `/v2/driver_applicant/${uniqueLink}/orientation/`

  const body = {
    orientation_id: orientationId,
  }

  try {
    const res = await axios.post(url, body)
    return camelCaseObjectKeys(res.data)
  } catch (error) {
    throw error
  }
}

export const loadOrientations = async uniqueLink => {
  const url = `/v2/driver_applicant/${uniqueLink}/orientation/`

  try {
    const res = await axios.get(url)
    return res.data.map(camelCaseObjectKeys)
  } catch (error) {
    throw error
  }
}

export const clearOrientation = async uniqueLink => {
  const url = `/v2/driver_applicant/${uniqueLink}/orientation/`

  try {
    const res = await axios.delete(url)
    return res
  } catch (error) {
    throw error
  }
}

export const getApplicationStatus = async uniqueLink => {
  const url = `/v1/dasher_applicants/${uniqueLink}/application_status/`

  try {
    const res = await axios.get(url)
    return camelCaseObjectKeys(res)
  } catch (error) {
    throw error
  }
}

export const activatePurchaseCard = async (dasherId, last4, delightNumber) => {
  const url = `/v1/dashers/${dasherId}/purchase_card/`

  const body = {
    last4,
    delight_number: parseInt(delightNumber, 10),
  }

  try {
    const res = await axios.post(url, body)
    return res
  } catch (error) {
    throw error
  }
}

export const updatePurchaseCard = async (dasherId, last4, delightNumber) => {
  const url = `/v1/dashers/${dasherId}/update_purchase_card/`

  const body = {
    last4,
    delight_number: delightNumber,
  }

  try {
    const res = await axios.post(url, body)
    return res
  } catch (error) {
    throw error
  }
}

export const getPurchaseCard = async dasherId => {
  const url = `/v1/dashers/${dasherId}/purchase_card/`

  try {
    const res = await axios.get(url)
    return res
  } catch (error) {
    throw error
  }
}

export const getStripeCredentials = async dasherId => {
  const url = `/v1/dashers/${dasherId}/stripe_credentials/`

  try {
    const res = await axios.get(url)
    return camelCaseObjectKeys(res)
  } catch (error) {
    throw error
  }
}
export const getPaymentAccountId = async dasherId => {
  const url = `/v1/dashers/${dasherId}/?fields=payment_account`
  try {
    const res = await axios.get(url)
    return res.data.payment_account
  } catch (error) {
    throw error
  }
}

export const updateBankAccount = async (paymentAccountId, token) => {
  const url = `/v1/payment_accounts/${paymentAccountId}/bank_accounts/`
  const payload = { enable_mfa: false, stripe_token: token.id }

  return await axios.post(url, payload)
}

export const createPaymentAccount = async data => {
  const url = `/v1/payment_accounts/`

  return await axios.post(url, data)
}

export const updatePaymentAccount = async (paymentAccountId, data) => {
  const url = `/v1/payment_accounts/${paymentAccountId}/`

  return await axios.patch(url, data)
}
export const getPaymentAccount = async paymentAccountId => {
  const url = `/v1/payment_accounts/${paymentAccountId}/`
  const res = await axios.get(url)
  return camelCaseObjectKeys(res.data)
}

/**
 * Stripe APIs
 */
export const uploadFileToStripe = async (stripeKey, payload) => {
  const url = 'https://uploads.stripe.com/v1/files'
  const token = `Bearer ${stripeKey}`

  const instance = axios.create()
  const config: AxiosRequestConfig = {
    headers: {
      Authorization: token,
    },
  }

  try {
    const res = await instance.post(url, payload, config)
    return camelCaseObjectKeys(res).data
  } catch (error) {
    throw error
  }
}

export const getWebviewExperience = async uniqueLink => {
  const url = `/v1/get_webview_experience/${uniqueLink}/`
  return await axios.get(url)
}

export const getWebviewExperiments = async uniqueLink => {
  const url = `/v1/get_webview_experiments/${uniqueLink}/`
  return await axios.get(url)
}

export const getWebviewExperiment = async (uniqueLink, experimentName) => {
  const url = `/v1/get_webview_experiments/${uniqueLink}/${experimentName}`
  return await axios.get(url)
}

export const getExperiment = async name => {
  const url = `/v2/experiments/${name}/`
  return await axios.get(url)
}

export const closeInAppEducation = async uniqueLink => {
  const url = `/v1/dasher_applicants/${uniqueLink}/close_in_app_education`
  return await axios.post(url)
}

/**
 * API request and format the list of delivery issues for a given Dasher.
 *
 * @param {string} dasherId
 * @returns {Promise<DeliveryIssueListItem[]>}
 */
export const getDeliveryIssuesList = async (
  dasherId: string
): Promise<DeliveryIssueListItem[]> => {
  const url = `/v1/dashers/${dasherId}/delivery_issues/`

  const { data } = await axios.get(url)
  return sortDeliveryIssuesListByNewest(formatDeliveryIssuesList(data))
}

/**
 * API request and format the full details of a specific delivery issue.
 *
 * @param {string} dasherId
 * @param {string} issueId
 * @returns {Promise<DeliveryIssue>}
 */
export const getDeliveryIssueDetails = async (
  dasherId: string,
  issueId: string
): Promise<DeliveryIssue> => {
  // tslint:disable-next-line:max-line-length
  const url = `/v1/dashers/${dasherId}/delivery_issues/${issueId}/?extra=steps&extra=issue_data`

  const response = await axios.get(url)
  return formatDeliveryIssueDetails(response.data)
}

/**
 * api request to submit a users dispute for a given delivery issue
 * @param dasherId dasher id
 * @param issueId delivery issue id
 * @param message custom user dispute message
 */
export const submitDeliveryIssueDispute = async (
  dasherId: string,
  issueId: string,
  message: string
) => {
  const url = `/v1/dashers/${dasherId}/delivery_issues/${issueId}/dispute/`
  return axios.post(url, { description: message })
}

/**
 * API post and request. First POST to mark a delivery issue as status: "read",
 * then request the same delivery issue details resource as an update.
 *
 * @param {string} dasherId
 * @param {string} issueId
 * @returns {Promise<DeliveryIssue>}
 */
export const markDeliveryIssueAsRead = async (
  dasherId: string,
  issueId: string
): Promise<DeliveryIssue> => {
  const url = `/v1/dashers/${dasherId}/delivery_issues/${issueId}/read/`

  await axios.post(url)
  return await getDeliveryIssueDetails(dasherId, issueId)
}

/**
 * Submit a debit card to enable Fast Pay
 *
 * @param {string} dasherId
 * @param {string} stripeToken
 * @param {boolean} enableMfa
 * @returns {Promise<FastPay>}
 */
export const submitFastPaySetup = async (
  dasherId: string,
  stripeToken: string,
  enableMfa?: boolean
) => {
  const url = `/v1/dashers/${dasherId}/payout_cards/`

  try {
    const res = await axios.post(
      url,
      snakeCaseObjectKeys({
        stripeToken,
        enableMfa,
      })
    )
    return res
  } catch (error) {
    throw error.response.data
  }
}

export const updateVehicle = async (
  vehicleId: number,
  data: VehiclePatchData
) => {
  const url = `/v1/vehicles/${vehicleId}/`
  return await axios.patch(url, snakeCaseObjectKeys(data))
}

export const postGetSales = async (uniqueLink: string, phone: string) => {
  const url = `/v1/dasher_applicants/${uniqueLink}/getsales`
  return await axios.post(url, { phone_number: phone })
}

export const vehicleOptOut = async (uniqueLink: string) => {
  const url = `/v1/dasher_applicants/${uniqueLink}/vehicle_opt_out/`
  return await axios.post(url)
}

export const updateDasherPhoneNumber = async (formattedPhoneNumber: string) => {
  const url = '/v1/users/me/'
  return await axios.patch(url, {
    phone_number: formattedPhoneNumber,
  })
}
export const markSafetyCourseComplete = async (uniqueLink: string) => {
  const url = `/v1/dasher_applicants/${uniqueLink}/close_dx_safety_course`
  return await axios.post(url, {})
}

declare const window: { view: { DX_TOOLS_API: string } }
const dxApiUrl = (): string | undefined => get(window, 'view.DX_API')

export const isMfaReady = async () => {
  const opts: AxiosRequestConfig = { baseURL: dxApiUrl() }
  const url = '/v3/mfa/ready'
  return await axios.get(url, opts)
}

export const submitIdv = async (uniqueLink: string, inquiryId: string, eventType: string) => {
  const opts: AxiosRequestConfig = { baseURL: dxApiUrl() }
  const url = '/v1/identity_verification'
  return await axios.post(url, camelCaseObjectKeys({
    'applicantId': uniqueLink,
    'uniqueLink': uniqueLink,
    'inquiryId': inquiryId,
    'eventType': eventType
  }), opts)
}
